// Lista de «Teléfono»: la tabla POS compartida con una fila por modelo que se despliega en sus
// variantes. La variante y su «Vender» abren el cajón de detalle; la venta arranca desde ahí.
import { useMemo, type ReactNode } from 'react';
import { Badge } from '@/kit';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { productTierLabel } from '@/shared/lib/format/productTier';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import { POS_KBD, PosProductTable } from '../../pos/components/PosProductTable';
import type { DataTableColumn } from '@/shared/patterns/DataTable';
import { availableOf, type PosSort, type PosSortKey } from '../../pos/lib/posCatalog';
import type { PosStat, PosStats } from '../../pos/lib/posFilters';
import {
    variantCountLabel,
    catalogEntries,
    modelTableRows,
    priceRangeLabel,
    rowSku,
    SOME_VARIANTS_LABEL,
    variantCoverage,
    type ModelTableRow,
} from '../../catalog/lib/modelGroups';
import { StatusPill } from '@/shared/patterns/Pills';
import {
    groupFinanciers,
    ModelNameCell,
    sellLabel,
    VARIANT_ROW_CLASS,
    VariantNameCell,
} from '../../catalog/components/ModelRowCells';
import { useExpandedModels } from '../../catalog/hooks/useExpandedModels';
import { financierChips } from '../lib/phoneCatalog';
import { isLastPhones, isSellable } from '../lib/phoneSale';
import { promotionBubbles, type PhonePromotions } from '../lib/phonePromotions';
import { PhonePromotionsCell } from './PhonePromotionsCell';

type PhoneSaleRow = ModelTableRow;
type PhoneSaleColumn = DataTableColumn<PhoneSaleRow, PosSortKey>;

const STAT_ITEMS: { key: PosStat; label: string; tone?: 'success' | 'primary' | 'warning' }[] = [
    { key: 'all', label: 'Todos' },
    { key: 'promo', label: 'Con promo', tone: 'primary' },
    { key: 'surekeep', label: 'SureKeep', tone: 'success' },
    { key: 'last', label: 'Últimas piezas', tone: 'warning' },
];

function FinancierBadges({ codes }: { codes: string[] }) {
    return (
        <span className="flex gap-1">
            {financierChips(codes).map((chip) => (
                <Badge key={chip.code} variant={chip.tone} size="xs" title={chip.name}>
                    {chip.short}
                </Badge>
            ))}
        </span>
    );
}

/** Piezas del modelo o de la variante; sin piezas, «Agotado». */
function stockCell(row: PhoneSaleRow): string | number {
    const stock = row.kind === 'model' ? row.group.stockTotal : availableOf(row.item);
    return stock > 0 ? stock : 'Agotado';
}

function stockClass(row: PhoneSaleRow): string | undefined {
    if (row.kind === 'model') return row.group.soldOut ? 'font-semibold text-danger' : undefined;
    if (availableOf(row.item) <= 0) return 'font-semibold text-danger';
    return isLastPhones(row.item) ? 'font-semibold text-warning' : undefined;
}

export function PhoneSaleTable({
    rows,
    stats,
    stat,
    onStatChange,
    sort,
    onSortToggle,
    onOpenDetail,
    selectedSku,
    promotionsOf,
    isLoading,
    isError,
    onRetry,
}: {
    rows: CatalogItem[];
    stats: PosStats;
    stat: PosStat;
    onStatChange: (next: PosStat) => void;
    sort: PosSort;
    onSortToggle: (key: PosSortKey) => void;
    onOpenDetail: (sku: string) => void;
    /** El teléfono con el cajón abierto, para marcar su fila. */
    selectedSku?: string | null;
    /** Todas las promos que le aplican al teléfono: venta, crédito y de toda la tienda. */
    promotionsOf: (item: CatalogItem) => PhonePromotions;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
}) {
    const entries = useMemo(() => catalogEntries(rows), [rows]);
    const { expanded, toggle } = useExpandedModels(entries, selectedSku ?? null);
    const tableRows = modelTableRows(entries, expanded);

    // La fila del modelo lleva la promo solo si aplica igual a todas sus variantes.
    const promoCell = (row: PhoneSaleRow): ReactNode => {
        if (row.kind !== 'model')
            return <PhonePromotionsCell promotions={promotionsOf(row.item)} />;
        const coverage = variantCoverage(row.group, (item) => {
            const promotions = promotionsOf(item);
            const labels = promotionBubbles(promotions).map((bubble) => bubble.label);
            return labels.join('|') || (promotions.undetailed ? 'undetailed' : '');
        });
        if (coverage.kind === 'some') {
            return (
                <StatusPill tone="info" title={SOME_VARIANTS_LABEL}>
                    {SOME_VARIANTS_LABEL}
                </StatusPill>
            );
        }
        const sample = coverage.kind === 'all' ? coverage.sample : row.group.defaultVariant;
        return <PhonePromotionsCell promotions={promotionsOf(sample)} />;
    };

    const columns: PhoneSaleColumn[] = [
        {
            key: 'model',
            label: 'Modelo',
            sortKey: 'model',
            cell: (row) =>
                row.kind === 'model' ? (
                    <ModelNameCell
                        group={row.group}
                        expanded={row.expanded}
                        onToggle={() => {
                            toggle(row.key);
                        }}
                    />
                ) : (
                    <VariantNameCell item={row.item} />
                ),
        },
        {
            key: 'variants',
            label: 'Variantes',
            width: '120px',
            cell: (row) => (row.kind === 'model' ? variantCountLabel(row.group) : ''),
            cellClassName: () => 'text-muted-foreground',
        },
        {
            key: 'tier',
            label: 'Gama',
            width: '100px',
            cell: (row) =>
                row.kind === 'model' ? productTierLabel(row.group.defaultVariant.tier) : '',
            cellClassName: () => 'text-muted-foreground',
        },
        {
            key: 'price',
            label: 'Precio',
            width: '150px',
            numeric: true,
            sortKey: 'price',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.kind === 'model'
                    ? priceRangeLabel(row.group)
                    : formatMoneyCents(row.item.cashPriceCents),
            cellClassName: () => 'font-semibold',
        },
        {
            key: 'stock',
            label: 'Stock',
            width: '70px',
            numeric: true,
            sortKey: 'stock',
            cell: stockCell,
            cellClassName: stockClass,
        },
        {
            key: 'financiers',
            label: 'Financieras',
            width: '120px',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.kind === 'model' ? (
                    <FinancierBadges codes={groupFinanciers(row.group)} />
                ) : (
                    <FinancierBadges codes={row.item.eligibleFinanciers} />
                ),
        },
        {
            key: 'promo',
            label: 'Promoción',
            width: '170px',
            cellOverflow: 'nowrap',
            cell: promoCell,
        },
        {
            key: 'action',
            label: <span className="sr-only">Vender</span>,
            width: '86px',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.kind === 'model' ? null : (
                    <div className="text-right">
                        <button
                            type="button"
                            disabled={!isSellable(row.item)}
                            aria-label={sellLabel(row.item)}
                            onClick={(event) => {
                                event.stopPropagation();
                                onOpenDetail(row.item.sku);
                            }}
                            className="inline-flex items-center rounded-[5px] bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Vender
                        </button>
                    </div>
                ),
        },
    ];

    return (
        <PosProductTable<PhoneSaleRow>
            rows={tableRows}
            columns={columns}
            rowKey={(row) => row.key}
            rowClassName={(row) => (row.kind === 'variant' ? VARIANT_ROW_CLASS : undefined)}
            resultCount={entries.length}
            stats={stats}
            statItems={STAT_ITEMS}
            stat={stat}
            onStatChange={onStatChange}
            sort={sort}
            onSortToggle={onSortToggle}
            onActivate={(row) => {
                if (row.kind === 'model') toggle(row.key);
                else onOpenDetail(row.item.sku);
            }}
            isRowSelected={(row) => rowSku(row) !== null && rowSku(row) === selectedSku}
            caption="Teléfonos de la tienda"
            emptyText="Ningún teléfono coincide con los filtros."
            hint={
                <>
                    <kbd className={POS_KBD}>Enter</kbd> abre la 1ª fila ·{' '}
                    <kbd className={POS_KBD}>Clic</kbd> despliega el modelo
                </>
            }
            isLoading={isLoading}
            isError={isError}
            onRetry={onRetry}
        />
    );
}
